<template>
    <div id="popup_table" 
        class="popup-table"
        style="height: fit-content;"
        :class="is_full_height ? 'full-height' : ''"
        :style="(is_full_width) ? 'width: 100%;' : ''"
    >
        <div 
            class="popup-table__wrapper"
            :class="is_minimize ? 'h-66px' : 'h-100vh'"
            style="min-height: 0;"
        >
            <div class="popup-table__container-title">
                <p>
                    {{ datetime_str }}
                </p>
                <h1>{{ current_data.title }}</h1>
                <v-btn
                    v-if="is_full_height"
                    class="maximize-btn"
                    color="white"
                    icon
                    style="display: inline-block;"
                    @click="is_minimize=!is_minimize"
                >
                    <v-icon color="white">
                        {{ is_minimize ? 'mdi-window-maximize' : 'mdi-window-minimize' }}
                    </v-icon>
                </v-btn>
                <v-btn
                    class="close-btn"
                    color="black"
                    icon
                    @click="close_dialog"
                >
                    <v-icon color="white">
                        mdi-close
                    </v-icon>
                </v-btn>
            </div>
            <div 
                class="popup-table__container"
                :class="is_minimize ? 'h-0vh' : 'h-100vh'"
            >
                <!-- 根據不同的項目，顯示不同的排版 -->
                <div class="popup-table__content-wrapper">
                    <div v-if="is_raw_html" 
                        v-html="current_data.content"
                        class="popup-table__content popup-table__content--image" 
                    />
                    <div v-else-if="popup_type === 'description_info' && COLUMNAR_TYPE.includes(product_sub_type)"
                        class="alert-table__wrapper"
                    >
                        <div v-for="({ name, value }) in current_data.content" 
                            :key="name"
                        >
                            <div class="alert-table__sub-header">
                                {{ name }}
                            </div>
                            <div v-html="value"
                                class="alert-table__content" 
                            />
                        </div>
                    </div>
                    <!-- 機場閃電警示 -->
                    <div v-else-if="popup_type === 'description_info' && product_sub_type === 'lightning_alert'"
                        class="alert-table__wrapper"
                    >
                        <div class="alert-table__content">
                            <p v-for="(item, key) in current_data.content" 
                                :key="key"
                            >
                                {{ item }}
                            </p>
                        </div>
                    </div>
                    <!-- 通常以九宮格表示, two_column 就是兩列 -->
                    <div v-else 
                        class="popup-table__content h-39vh" 
                        :class="{
                            'two-column': is_two_column
                        }"
                    >
                        <div v-for="datum in current_data.content" 
                            :key="datum.name" 
                            class="popup-table__content-item"
                        >
                            <template v-if="datum.name !== '時間' && popup_type === 'gis_popup_data'">
                                <h6>{{ datum.name }}</h6>
                                <p>{{ datum.value }}</p>
                            </template>
                            <template v-else-if="datum.name !== '警示條件' && popup_type === 'description_info'">
                                <h6>{{ datum.name }}</h6>
                                <p v-html="datum.value" 
                                    :class="{
                                        'small-font-size': small_font_size_column.includes(datum.name)
                                    }" 
                                />
                            </template>
                        </div>
                    </div>
                    <template v-if="popup_type === 'description_info' && product_sub_type === 'monitor'">
                        <div class="popup-table__content one-column">
                            <h6>{{ (rain_notice_content.name === "警示條件") ? rain_notice_content.name : '無警示條件' }}</h6>
                            <div v-if="rain_notice_content.name === '警示條件'">
                                <div 
                                    v-for="(notice, index) in rain_notice_content.value" 
                                    :key="index" 
                                    class="popup-table__content-item"
                                >
                                    <p>{{ notice.name }}：</p>
                                    <p v-html="notice.value" />
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <template v-if="GIS_CHART_TYPE.includes(gis_popup_type)">
                <v-btn @click="$emit('show-gis-diagram')" 
                    class="diagram-btn" 
                >
                    {{ $t('__gis.diagram') }}
                </v-btn>
            </template>
        </div>
    </div>
</template>

<script>
import { get_typhoon_name_from_popup, transform_ty_invalid_value } from '@/utils/emap/sub_type/ty_track.js'
import { transform_timestamp_to_customized_str } from '@/utils/common/custom_time.js'
import intersection from 'lodash/intersection'
import { mapState } from 'vuex'
export default {
    name: 'PopupTable',

    props: {
        gis_popup_data: {
            type: Object,
            default: () => ({}),
        },

        gis_popup_type: {
            type: String,
            default: '',
            require: true,
        },

        popup_type: {
            type: String,
            default: '',
            require: true,
        },

        description_info: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            TITLE_COLUMN_KEY: ['CITY', 'TOWN', 'STNM', 'STID', 'CITY_EN', 'TOWN_EN', 'STNM_EN'],

            COLUMNAR_TYPE: ['alert_inmap', 'radar_county_warning'],

            GIS_CHART_TYPE: ['station', 'auto', 'gauge'],

            is_minimize: false,
        }
    },

    computed: {
        ...mapState(['language']),

        ...mapState('emap', ['current_timestamp']),

        datetime_str() {
            return transform_timestamp_to_customized_str(this.current_timestamp, (datetime_str_obj) => {
                const { yyyy, mm, dd, HH, MM } = datetime_str_obj
                return `${yyyy}-${mm}-${dd} ${HH}:${MM}`
            })
        },

        current_data() {
            if (this.popup_type === 'gis_popup_data') {
                return {
                    title: this.get_gis_title(),
                    content: this.get_gis_content(),
                }
            }

            if (this.popup_type === 'description_info') {
                return {
                    title: this.get_description_title(),
                    content: this.get_description_content(),
                }
            }

            return {}
        },

        product_sub_type() {
            return this.description_info.product_sub_type
        },

        is_raw_html() {
            return this.description_info.raw_html
        },

        is_two_column() {
            const is_gis_two_column = ['gauge', 'flight'].includes(this.gis_popup_type)
            const is_description_two_column = this.product_sub_type === 'monitor'
            return is_gis_two_column || is_description_two_column
        },

        rain_notice_content() {
            return this.current_data.content.slice(-1)[0]
        },

        small_font_size_column() {
            // TODO: 英文
            return ['路段']
        },

        is_full_width() {
            return this.product_sub_type === 'ty_track'
        },

        is_full_height() {
            return [
                '海象預報',
                '一級監控橋梁雨量',
                '一級監控路段雨量',
                '二級監控路段雨量',
                '淹水泥流水瀑',
                '全流域雨量估計',
                '浪襲監控路段',
                '未來6個月集水區雨量-ECMWF',
            ].includes(this.current_data.title)
        },
    },

    methods: {
        get_gis_display_data() {
            const { popup: popup_column, feature } = this.gis_popup_data
            const station_data = feature.properties

            return popup_column.map((item) => {
                const joiner = item.join ?? ' '
                const values = item.column.map((key) => {
                    const v = station_data[key]
                    return v !== undefined && v !== null && v !== '' ? v : '--'
                })

                const all_empty = values.every((v) => v === '--')
                const column_value = all_empty ? '--' : values.join(joiner)

                return {
                    ...item,
                    value: column_value,
                }
            })
        },

        get_gis_title() {
            const gis_display_data = this.get_gis_display_data()
            return gis_display_data
                .filter(({ column }) => intersection(column, this.TITLE_COLUMN_KEY).length > 0)
                .reduce((accum, { value }) => `${accum} ${value}`, '')
        },

        get_gis_content() {
            // 去除時間
            const gis_display_data = this.get_gis_display_data()
            let content = gis_display_data.filter(
                ({ column }) => intersection(column, this.TITLE_COLUMN_KEY).length === 0
            )
            const time_index = content.findIndex(({ name }) => name === '時間')
            time_index !== -1 && content.splice(time_index, 1)

            return content
        },

        get_description_title() {
            const { product_sub_type } = this.description_info

            if (product_sub_type === 'ty_track') {
                const { tw, en } = get_typhoon_name_from_popup(this.description_info.popup_object)
                const typhoon_name = this.language === 'en' ? `${en} ${tw}` : `${tw} ${en}`
                return `${this.description_info.pane_text} ${typhoon_name}`
            }

            return this.description_info.pane_text
        },

        get_description_content() {
            const { product_sub_type } = this.description_info

            if (['alert_inmap', 'radar_county_warning', 'convection', 'lightning'].includes(product_sub_type)) {
                return this.transform_object_to_array(this.description_info.popup_object)
            }

            if (product_sub_type === 'ty_track') {
                return this.get_ty_track_content(this.description_info.popup_object)
            }

            // html 要額外做各自的處理
            if (product_sub_type === 'monitor') {
                return this.get_monitor_content(this.description_info.html)
            }

            if (product_sub_type === 'max_rain') {
                const description_html = this.description_info.html.split('</br>')
                return this.transform_to_array_by_colon(description_html)
            }

            if (product_sub_type === 'lightning_alert') {
                return this.description_info.html.split('\n').filter((o) => o)
            }

            return this.description_info.html
        },

        transform_to_array_by_colon(description_html) {
            return description_html.map((item) => {
                if (!item.includes(':')) {
                    return {
                        name: '',
                        value: '',
                    }
                }

                return {
                    name: item.split(':')[0],
                    value: item.split(':')[1],
                }
            })
        },

        transform_object_to_array(description_object) {
            return Object.entries(description_object).map(([name, value]) => ({ name, value }))
        },

        get_ty_track_content(popup_object) {
            const popup_object_transform_null = Object.entries(popup_object).reduce((accumulator, [key, value]) => {
                return {
                    ...accumulator,
                    [key]: transform_ty_invalid_value(value),
                }
            }, {})

            const {
                cwa_ty_no,
                cwa_td_no,
                fix_time,
                coordinate,
                pressure,
                max_gust_speed,
                max_wind_speed,
                circle_of_15ms,
                circle_of_25ms,
                moving_speed,
                moving_direction,
                forecast_data,
                init_time,
            } = popup_object_transform_null

            const typhoon_name_obj = get_typhoon_name_from_popup(this.description_info.popup_object)
            const time_key = forecast_data ? init_time : fix_time
            const time_text = `${time_key.slice(0, 16)} (UTC+${time_key.slice(20, 27)})`
            const [lat, lon] = coordinate
            const coordinate_text = `${lat}E, ${lon}N`

            let ty_popup_object = {
                '氣象署颱風/熱帶氣旋編號': `${cwa_ty_no} / ${cwa_td_no}`,
                國際颱風名稱: typhoon_name_obj.en,
                資料時間: time_text,
                經緯度: coordinate_text,
                '中心氣壓(hpa)': pressure,
                '最大陣風(m/s)': max_gust_speed,
                '最大風速(m/s)': max_wind_speed,
                '七級/十級風暴風半徑(km)': `${circle_of_15ms} / ${circle_of_25ms}`,
                未來移向移速: `${moving_speed} km/h, ${moving_direction}`,
            }

            if (this.language === 'en') {
                ty_popup_object = {
                    'Typhoon No.': `${cwa_ty_no} / ${cwa_td_no}`,
                    'Typhoon name': typhoon_name_obj.en,
                    'Data time': time_text,
                    Coordinate: coordinate_text,
                    'Minimum Pressure(hpa)': pressure,
                    Gust: max_gust_speed,
                    'Max Sustained Wind': max_wind_speed,
                    'Radius of over 15m/s / 26.5m/s': `${circle_of_15ms} / ${circle_of_25ms}`,
                    'Movement / Movement Speed': `${moving_speed} km/h, ${moving_direction}`,
                }
            }

            return this.transform_object_to_array(ty_popup_object)
        },

        get_monitor_content(description_html) {
            const description_html_split = this._split_table_html_tag(description_html)

            const new_description_html = description_html_split.map((item) => {
                const name = item.indexOf('tdtd') !== -1 ? item.split('tdtd')[0] : ''
                let value = item.indexOf('tdtd') !== -1 ? item.split('tdtd')[1] : ''
                if (name === '路段') {
                    value = value.replaceAll('br', '<br/>')
                }
                if (name === '警示條件') {
                    value = value
                        .replaceAll(/([()]*)/gi, '')
                        .replaceAll('br', '<br/>')
                        .replaceAll('OR', 'or')
                        .replaceAll('AND', '</br>&')
                        .split('<br/><br/>')
                        .map((value_item) => {
                            return {
                                name: value_item.split(':')[0],
                                value: value_item.split(':')[1],
                            }
                        })
                }
                return { name, value }
            })

            // 刪去空的
            new_description_html.splice(0, 1)
            // 刪去警示
            new_description_html.splice(0, 1)

            return new_description_html
        },

        _split_table_html_tag(description_html) {
            const regex = /([</>]*)/gi
            return description_html
                .replaceAll(regex, '')
                .replaceAll('table', '')
                .replaceAll('tdtr', '')
                .split('trtd')
        },

        close_dialog() {
            const event_name =
                this.popup_type === 'gis_popup_data' ? 'close-gis-popup-table' : 'close-geojson-popup-table'
            this.$emit(event_name)
        },
    },
}
</script>

<style lang="scss" scoped>
@import '../../../../css/emap/gis/popup';

.alert-table {
    &__wrapper {
        margin: 0.5rem;
        font-family: NotoSansCJKtc;
        background: #fff;
        min-height: 39vh;
    }

    &__sub-header {
        margin: 0.5rem;
        font-size: 20px;
        font-weight: 600;
        letter-spacing: 0.8px;
        line-height: 1.5;
        text-align: left;
        color: var(--base-blue);
    }

    &__content {
        margin: 0.5rem;
        letter-spacing: 0.6px;
        line-height: 1.43;
        color: var(--base_black);
    }
}
</style>
